import { type AxiosRequestConfig, isAxiosError } from 'axios';
import type {
  CommunityInviteChannel,
  InviteAdminApi,
  InviteAdminScope,
} from './inviteAdminContract';

const issuer = '/api/v1/invite-issuance/admin';
const community = '/api/v1/landing-waitlist/admin/channels';
const issuerScope = (scope: InviteAdminScope) => ({
  params: { appId: scope.appId },
});
const communityScope = (scope: InviteAdminScope) => ({
  headers: { brand_id: scope.brandId },
});

interface JsonHttpClient {
  get<T>(path: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(path: string, body: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T>(path: string, body: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export function createInviteAdminApi(api: JsonHttpClient): InviteAdminApi {
  return {
    async policy(selectedScope) {
      try {
        return await api.get(`${issuer}/policy`, issuerScope(selectedScope));
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 503 &&
          error.response.data?.reason === 'not_configured'
        )
          return null;
        throw error;
      }
    },
    async savePolicy(selectedScope, policy) {
      await api.put(
        `${issuer}/policy`,
        { channel: '', policy },
        issuerScope(selectedScope),
      );
    },
    statistics: (selectedScope) =>
      api.get(`${issuer}/stats`, issuerScope(selectedScope)),
    applications: (selectedScope, beforeId) =>
      api.get(`${issuer}/applications`, {
        ...issuerScope(selectedScope),
        params: {
          appId: selectedScope.appId,
          ...(beforeId ? { beforeId } : {}),
        },
      }),
    followups: (selectedScope, offset = 0) =>
      api.get(`${issuer}/followups`, {
        ...issuerScope(selectedScope),
        params: { appId: selectedScope.appId, offset },
      }),
    channels: (selectedScope, beforeId = '0') =>
      api.post(
        `${community}/list`,
        { beforeId, limit: 100 },
        communityScope(selectedScope),
      ),
    async createChannel(selectedScope, input) {
      const reply: { channel: CommunityInviteChannel } = await api.post(
        `${community}/create`,
        input,
        communityScope(selectedScope),
      );
      return reply.channel;
    },
    async updateChannel(selectedScope, input) {
      const reply: { channel: CommunityInviteChannel } = await api.post(
        `${community}/update`,
        input,
        communityScope(selectedScope),
      );
      return reply.channel;
    },
  };
}
